/**
 * useContactTags - Hook para gerenciar tags de um contato
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getContactTags, updateContactTags, getAvailableTags } from '../services/contacts.service';
import { toast } from 'sonner';

/**
 * Tags do contato para o header. Chave PRÓPRIA (`['contact', id, 'tags']`) —
 * NÃO reusar `['contact', id]`, que outros consumidores leem como o contato
 * inteiro; compartilhar a chave faria um sobrescrever o cache do outro com um
 * shape diferente.
 */
export function useContactTags(contactId?: string) {
  return useQuery({
    queryKey: ['contact', contactId, 'tags'],
    queryFn: () => getContactTags(contactId!),
    enabled: !!contactId,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Hook para buscar todas as tags disponíveis no sistema
 */
export const useAvailableTags = (enabled: boolean = true) => {
  return useQuery({
    queryKey: ['available-tags'],
    queryFn: getAvailableTags,
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook para atualizar tags de um contato
 */
export const useUpdateContactTags = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contactId, tags }: { contactId: string; tags: string[] }) =>
      updateContactTags(contactId, tags),
    onSuccess: (_data, variables) => {
      // Invalidar queries para forçar refetch
      queryClient.invalidateQueries({ queryKey: ['contact', variables.contactId] });
      // Chave PRÓPRIA das tags (C8) — sem isto, salvar tag no TagsDialog não
      // reflete no header até a próxima navegação.
      queryClient.invalidateQueries({ queryKey: ['contact', variables.contactId, 'tags'] });
      queryClient.invalidateQueries({ queryKey: ['conversation'] }); // Conversas podem ter referência ao contact
      queryClient.invalidateQueries({ queryKey: ['available-tags'] }); // Atualizar lista de tags disponíveis

      toast.success('Tags atualizadas com sucesso');
    },
    onError: (error: any) => {
      console.error('Erro ao atualizar tags:', error);
      toast.error(error?.response?.data?.message || 'Erro ao atualizar tags');
    },
  });
};
